import React from "react";
import { Skeleton } from "@progress/kendo-react-indicators";
import {
  Chart,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartLegend,
  ChartSeries,
  ChartSeriesItem,
  ChartTooltip,
  ChartValueAxis,
  ChartValueAxisItem,
} from "@progress/kendo-react-charts";
import { Path } from "@progress/kendo-drawing";

const BAR_CORNER_RADIUS = 4;
const BAR_FILL_OPACITY = 0.85;
// Non-zero values whose computed bar length rounds below this many pixels are
// bumped up to it, so a tiny value (e.g. 1 next to 40K) still shows a visible
// sliver instead of disappearing. Zero values remain invisible.
const MIN_VISIBLE_BAR_LENGTH = 3;

const createRoundedBarVisual = (isHorizontal) => (e) => {
  const size = e.rect.size;
  const value = Number(e.value);
  const hasNonZeroValue = Number.isFinite(value) && value !== 0;

  let width = size.width;
  let height = size.height;
  let x = e.rect.origin.x;
  let y = e.rect.origin.y;

  if (hasNonZeroValue) {
    if (isHorizontal) {
      if (width < MIN_VISIBLE_BAR_LENGTH) width = MIN_VISIBLE_BAR_LENGTH;
    } else if (height < MIN_VISIBLE_BAR_LENGTH) {
      // Keep the bar anchored to its baseline while growing it upward.
      y = y + height - MIN_VISIBLE_BAR_LENGTH;
      height = MIN_VISIBLE_BAR_LENGTH;
    }
  }

  if (!width || !height) {
    return e.createVisual();
  }

  const r = Math.min(BAR_CORNER_RADIUS, width / 2, height / 2);
  const path = new Path({
    fill: { color: e.options.color, opacity: BAR_FILL_OPACITY },
    stroke: null,
  });

  if (isHorizontal) {
    path
      .moveTo(x, y)
      .lineTo(x + width - r, y)
      .arc(270, 360, r, r, false)
      .lineTo(x + width, y + height - r)
      .arc(0, 90, r, r, false)
      .lineTo(x, y + height)
      .close();
  } else {
    path
      .moveTo(x, y + height)
      .lineTo(x, y + r)
      .arc(180, 270, r, r, false)
      .lineTo(x + width - r, y)
      .arc(270, 360, r, r, false)
      .lineTo(x + width, y + height)
      .close();
  }

  return path;
};

const HIDDEN_GRID_LINES = { visible: false };
const VISIBLE_GRID_LINES = { visible: true };
const EXPANDED_CHART_STYLE = { height: "calc(100vh - 190px)" };
const DEFAULT_CHART_STYLE = { height: "100%" };
const CHART_AREA = { margin: { top: 6, bottom: 6, left: 6, right: 6 } };

const formatAxisValue = (value, formatter) => {
  const num = Number(value);
  if (Number.isFinite(num) && num === 0) return "";
  return (formatter || ((v) => v))(value);
};

const ChartLoader = () => (
  <div className="eventdash-batch-chart-loader">
    <Skeleton shape={"text"} style={{ width: "45%", marginBottom: 8 }} />
    <Skeleton shape={"text"} style={{ width: "30%", marginBottom: 18 }} />
    <Skeleton shape={"rectangle"} style={{ width: "100%", height: "210px" }} />
  </div>
);

const getAxisScaleConfig = (seriesList, visibilityMap, axisName = null, tickCount = 5) => {
  const visibleValues = (seriesList || [])
    .filter((seriesItem) => visibilityMap[seriesItem.name] !== false)
    .filter((seriesItem) => !axisName || (seriesItem.axis || "leftAxis") === axisName)
    .flatMap((seriesItem) => (Array.isArray(seriesItem.data) ? seriesItem.data : []))
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));

  if (!visibleValues.length) {
    return { min: undefined, max: undefined, majorUnit: undefined };
  }

  const rawMax = Math.max(...visibleValues, 0);
  if (rawMax <= 0) {
    return { min: 0, max: 1, majorUnit: 1 };
  }

  const roughStep = Math.max(1, Math.ceil(rawMax / tickCount));
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const normalized = roughStep / magnitude;
  const stepBase = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  const majorUnit = stepBase * magnitude;
  const max = Math.ceil(rawMax / majorUnit) * majorUnit + majorUnit;

  return { min: 0, max, majorUnit };
};

const getSyncedDualAxisConfig = (seriesList, visibilityMap) =>
  getAxisScaleConfig(seriesList, visibilityMap, null, 6);

const DashboardChart = ({
  categories = [],
  categoryTooltips = {},
  series = [],
  loading = false,
  noData = false,
  isExpanded = false,
  visibility = {},
  onToggleSeries,
  valueFormatter,
  tooltipValueFormatter,
  tooltipShowCategory = true,
  secondaryValueFormatter,
  categoryFormatter,
  labelRotation = 0,
  valueLabelRotation = 0,
  useDualAxis = false,
  syncDualAxisScale = false,
  leftAxisTitle = "",
  rightAxisTitle = "",
  leftAxisMajorUnit,
  categoryAxisTitle = "",
  isPieChart = false,
  invertCategoryOrder = false,
  horizontalBarChart = false,
  showLegend = true,
  barGap,
  barSpacing,
}) => {
  const chartCanvasRef = React.useRef(null);
  const chartRef = React.useRef(null);
  const chartDataSignature = React.useMemo(() => {
    const source = `${categories.join("|")}~${series
      .map((seriesItem) => `${seriesItem.name}:${Array.isArray(seriesItem.data) ? seriesItem.data.join(",") : ""}`)
      .join(";")}`;
    let hash = 0;
    for (let i = 0; i < source.length; i += 1) {
      hash = (hash * 31 + source.charCodeAt(i)) | 0;
    }
    return hash;
  }, [categories, series]);
  const hasHorizontalBar =
    horizontalBarChart || series.some((seriesItem) => seriesItem.type === "bar");
  const categoryAxisReverse = Boolean(invertCategoryOrder);
  const horizontalBarValueAxisCrossing = categoryAxisReverse
    ? Math.max(categories.length, 1)
    : 0;
  const leftAxisConfig = React.useMemo(() => {
    if (useDualAxis && syncDualAxisScale) {
      return getSyncedDualAxisConfig(series, visibility);
    }
    if (leftAxisMajorUnit != null) {
      return { min: undefined, max: undefined, majorUnit: leftAxisMajorUnit };
    }
    if (useDualAxis) {
      return getAxisScaleConfig(series, visibility, "leftAxis", 5);
    }
    return { min: undefined, max: undefined, majorUnit: undefined };
  }, [leftAxisMajorUnit, series, syncDualAxisScale, useDualAxis, visibility]);
  const rightAxisConfig = React.useMemo(() => {
    if (!useDualAxis) {
      return { min: undefined, max: undefined, majorUnit: undefined };
    }
    if (syncDualAxisScale) {
      return getSyncedDualAxisConfig(series, visibility);
    }
    return getAxisScaleConfig(series, visibility, "rightAxis", 5);
  }, [series, syncDualAxisScale, useDualAxis, visibility]);
  const axisTitleConfig = React.useMemo(
    () => ({
      visible: true,
      font: "11px Poppins, sans-serif",
      color: "#6b7280",
    }),
    []
  );
  const categoryAxisCrossingValue = React.useMemo(() => {
    if (hasHorizontalBar) return undefined;
    return useDualAxis ? [0, Math.max(categories.length, 1)] : 0;
  }, [hasHorizontalBar, useDualAxis, categories.length]);
  const categoryAxisTitleOption = React.useMemo(
    () => (categoryAxisTitle ? { ...axisTitleConfig, text: categoryAxisTitle } : undefined),
    [axisTitleConfig, categoryAxisTitle]
  );
  const leftAxisTitleOption = React.useMemo(
    () => (leftAxisTitle ? { ...axisTitleConfig, text: leftAxisTitle } : undefined),
    [axisTitleConfig, leftAxisTitle]
  );
  const rightAxisTitleOption = React.useMemo(
    () => (rightAxisTitle ? { ...axisTitleConfig, text: rightAxisTitle } : undefined),
    [axisTitleConfig, rightAxisTitle]
  );
  const categoryAxisLabels = React.useMemo(
    () => ({
      rotation: labelRotation,
      skip: 0,
      step: 1,
      font: "10px/1.1 Poppins, sans-serif",
      content: (e) => categoryFormatter(e.value),
    }),
    [labelRotation, categoryFormatter]
  );
  const leftAxisLabels = React.useMemo(
    () => ({
      content: (e) => formatAxisValue(e.value, valueFormatter),
      rotation: valueLabelRotation,
    }),
    [valueFormatter, valueLabelRotation]
  );
  const rightAxisLabels = React.useMemo(
    () => ({
      content: (e) => formatAxisValue(e.value, secondaryValueFormatter || valueFormatter),
      rotation: valueLabelRotation,
    }),
    [secondaryValueFormatter, valueFormatter, valueLabelRotation]
  );

  const decorateAxisLabels = React.useCallback(() => {
    const container = chartCanvasRef.current;
    if (!container || !categoryTooltips || Object.keys(categoryTooltips).length === 0) {
      return;
    }

    const normalizeLabel = (text) => String(text || "").replace(/[\s\u200b]+/g, "").toLowerCase();
    const lookup = new Map();
    Object.keys(categoryTooltips).forEach((category) => {
      const batchName = categoryTooltips[category];
      const fullKey = normalizeLabel(category);
      if (fullKey) lookup.set(fullKey, batchName);
      String(category)
        .split("\n")
        .forEach((line) => {
          const lineKey = normalizeLabel(line);
          if (lineKey && !lookup.has(lineKey)) lookup.set(lineKey, batchName);
        });
    });

    const svg = container.querySelector("svg");
    if (!svg) return;
    const svgNs = "http://www.w3.org/2000/svg";
    svg.querySelectorAll("text").forEach((node) => {
      const batchName = lookup.get(normalizeLabel(node.textContent));
      if (!batchName) return;
      const existing = node.querySelector("title");
      if (existing && existing.textContent === batchName) {
        node.style.cursor = "pointer";
        return;
      }
      existing?.remove();
      const titleEl = document.createElementNS(svgNs, "title");
      titleEl.textContent = batchName;
      node.appendChild(titleEl);
      node.style.cursor = "pointer";
    });
  }, [categoryTooltips]);

  React.useEffect(() => {
    const frame = requestAnimationFrame(decorateAxisLabels);
    return () => cancelAnimationFrame(frame);
  }, [decorateAxisLabels, categories, series, visibility, isExpanded]);

  const handleChartRender = React.useCallback(() => {
    requestAnimationFrame(decorateAxisLabels);
  }, [decorateAxisLabels]);

  React.useEffect(() => {
    const container = chartCanvasRef.current;
    if (!container || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      chartRef.current?.chartInstance?.resize();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const renderTooltip = React.useCallback((context) => {
    const point = context?.point || {};
    const tooltipFormatter = tooltipValueFormatter || valueFormatter;
    const rawCategory = context?.category ?? point?.category;
    const tooltipCategory =
      rawCategory === null || rawCategory === undefined
        ? ""
        : String(rawCategory)
            .replace(/\s*\n\s*/g, " ")
            .trim();
    const normalizedTooltipCategory = tooltipCategory.toLowerCase().replace(/[_\s]+/g, " ").trim();
    const shouldOmitCategory =
      normalizedTooltipCategory === "record name" ||
      normalizedTooltipCategory === "record names" ||
      normalizedTooltipCategory === "record" ||
      normalizedTooltipCategory === "records";
    const categoryPrefix =
      tooltipShowCategory && tooltipCategory && !shouldOmitCategory ? `${tooltipCategory} ` : "";

    const seriesName =
      context?.series?.name ??
      point?.series?.name ??
      context?.seriesName ??
      point?.seriesName ??
      context?.dataItem?.series_name ??
      point?.dataItem?.series_name ??
      "";

    const rawValue =
      context?.value ??
      point?.value ??
      context?.dataItem?.value ??
      point?.dataItem?.value ??
      context?.dataItem?.series_data ??
      point?.dataItem?.series_data ??
      context?.dataItem?.seriesData ??
      point?.dataItem?.seriesData;

    const value =
      rawValue && typeof rawValue === "object"
        ? rawValue.value ?? rawValue.y ?? rawValue.amount ?? rawValue.data ?? 0
        : rawValue;

    const batchTooltipName = (() => {
      if (!categoryTooltips || rawCategory === null || rawCategory === undefined) return "";
      if (categoryTooltips[rawCategory]) return categoryTooltips[rawCategory];
      const normalize = (text) => String(text || "").replace(/[\s\u200b]+/g, "").toLowerCase();
      const target = normalize(rawCategory);
      const match = Object.keys(categoryTooltips).find((key) => normalize(key) === target);
      return match ? categoryTooltips[match] : "";
    })();

    return (
      <span>
        {batchTooltipName ? (
          <>
            <strong>{batchTooltipName}</strong>
            <br />
          </>
        ) : null}
        {categoryPrefix}
        {seriesName}: {tooltipFormatter(value)}
      </span>
    );
  }, [categoryTooltips, tooltipShowCategory, tooltipValueFormatter, valueFormatter]);

  if (loading) {
    return <ChartLoader />;
  }

  if (noData) {
    return <div className="eventdash-chart-nodata">No Data Found</div>;
  }

  return (
    <div className={`eventdash-chart-renderer ${isExpanded ? "eventdash-chart-renderer--expanded" : ""}`}>
      {showLegend ? (
        <div className="chart-legend-set">
          {series
            .filter((seriesItem) => !seriesItem.hideFromLegend)
            .map((seriesItem) => (
            <span
              className="chart-legend-item"
              data-no-drag="true"
              key={seriesItem.name}
              style={{
                color: seriesItem.color,
                opacity: visibility[seriesItem.name] !== false ? 1 : 0.5,
              }}
              onClick={() => onToggleSeries(seriesItem.name)}
            >
              <span>{seriesItem.name}</span>
            </span>
          ))}
        </div>
      ) : null}
      <div className="eventdash-chart-canvas" ref={chartCanvasRef}>
        <Chart
          key={
            hasHorizontalBar
              ? `hbar-${categoryAxisReverse ? "rev" : "std"}-${categories.length}-${chartDataSignature}`
              : `chart-${categories.length}-${chartDataSignature}`
          }
          ref={chartRef}
          renderAs="svg"
          transitions={false}
          style={isExpanded ? EXPANDED_CHART_STYLE : DEFAULT_CHART_STYLE}
          chartArea={CHART_AREA}
          onRender={handleChartRender}
        >
          <ChartLegend visible={false} />
          <ChartTooltip render={renderTooltip} />
          {!isPieChart ? (
            <ChartCategoryAxis>
              <ChartCategoryAxisItem
                categories={categories}
                reverse={categoryAxisReverse}
                title={categoryAxisTitleOption}
                {...(categoryAxisCrossingValue === undefined
                  ? {}
                  : { axisCrossingValue: categoryAxisCrossingValue })}
                labels={categoryAxisLabels}
                majorGridLines={HIDDEN_GRID_LINES}
              />
            </ChartCategoryAxis>
          ) : null}
          {!isPieChart ? (
            <ChartValueAxis>
              <ChartValueAxisItem
                name="leftAxis"
                min={leftAxisConfig.min}
                max={leftAxisConfig.max}
                majorUnit={leftAxisConfig.majorUnit}
                {...(hasHorizontalBar ? { axisCrossingValue: horizontalBarValueAxisCrossing } : {})}
                title={leftAxisTitleOption}
                labels={leftAxisLabels}
                majorGridLines={VISIBLE_GRID_LINES}
              />
              {useDualAxis ? (
                <ChartValueAxisItem
                  name="rightAxis"
                  position="right"
                  min={rightAxisConfig.min}
                  max={rightAxisConfig.max}
                  majorUnit={rightAxisConfig.majorUnit}
                  title={rightAxisTitleOption}
                  labels={rightAxisLabels}
                  majorGridLines={HIDDEN_GRID_LINES}
                />
              ) : null}
            </ChartValueAxis>
          ) : null}
          <ChartSeries>
            {series
              .filter((seriesItem) => visibility[seriesItem.name] !== false)
              .map((seriesItem) => {
                const seriesType = seriesItem.type || "column";
                const isBarType = seriesType === "column" || seriesType === "bar";
                return (
                  <ChartSeriesItem
                    key={seriesItem.name}
                    type={seriesType}
                    name={seriesItem.name}
                    data={seriesItem.data}
                    color={seriesItem.color}
                    stack={seriesItem.stack}
                    axis={seriesItem.axis || "leftAxis"}
                    field={seriesItem.field}
                    categoryField={seriesItem.categoryField}
                    gap={isBarType ? barGap : undefined}
                    spacing={isBarType ? barSpacing : undefined}
                    visual={isBarType ? createRoundedBarVisual(seriesType === "bar") : undefined}
                    labels={
                      seriesItem.type === "pie"
                        ? {
                            visible: true,
                            position: "outsideEnd",
                            background: "none",
                            content: (e) => `${e.category}: ${valueFormatter(e.value)}`,
                          }
                        : undefined
                    }
                    markers={seriesItem.type === "line" ? { visible: true, size: 5 } : undefined}
                  />
                );
              })}
          </ChartSeries>
        </Chart>
      </div>
    </div>
  );
};

export default React.memo(DashboardChart);
